import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  gaEventPlaylistShare,
  type PlaylistForAnalytics,
} from "../../../tools/analytics/domains/playlist.analytics";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useOnboardingGuard } from "../../user/hooks/useOnboardingGuard";
import { apiClient, useApiRevalidate } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { copyToClipboard, shareUrl } from "../../app/utils/shareUrl";
import { REDIRECT_HOST } from "../../../config";

export type UseSharePlaylist = {
  /** The system share sheet (mobile). */
  triggerShare: () => Promise<void>;
  /** Copies the link and says so in a toast (desktop). */
  copyLink: () => Promise<void>;
};

export function useSharePlaylist(
  roomId: string | null,
  playlist: PlaylistForAnalytics | null,
  analyticsListName: AnalyticsItemList
): UseSharePlaylist {
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation();
  const onboardingGuard = useOnboardingGuard();
  const revalidate = useApiRevalidate();

  /** The link to send: a short link, or the page's own when that fails. */
  const shareLink = (sharedPlaylist: PlaylistForAnalytics): Promise<string> => {
    const sharedId = currentUser === null ? "" : currentUser.id;
    // The same `roomId` the share is recorded against. It was reaching the
    // API and not the link, so the recipient lost the room.
    const path =
      roomId === null
        ? `/playlist/public/${sharedPlaylist.id}`
        : `/room/playlist/${roomId}/${sharedPlaylist.id}`;
    const longUrl = `${globalThis.location.origin}${path}?shared=${sharedId}`;
    return apiClient
      .post("/webapp/private/share-links", {
        json: { type: "playlist", id: sharedPlaylist.id, roomId },
      })
      .then(({ body }) => `https://${REDIRECT_HOST}/${body}`)
      .catch(() => longUrl);
  };

  const profileGuardMessage = t(
    "🔒 Please complete your profile first to share this playlist."
  );

  const triggerShare = async (): Promise<void> => {
    try {
      if (playlist === null) throw new Error("Invalid playlist.");

      if (!onboardingGuard(profileGuardMessage)) return;
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = apiClient
        .patch("/webapp/public/playlists/share/:playlistId", {
          param: { playlistId: playlist.id },
          json: roomId === null ? {} : { roomId },
        })
        .then(() => shareLink(playlist));
      const isOwnedPlaylistPath =
        history.location.pathname.startsWith("/profile/playlist");
      const text = isOwnedPlaylistPath
        ? t("Discover my playlist {{title}} on Juisci now!", {
            title: playlist.title,
          })
        : t("Discover the playlist {{title}} on Juisci now!", {
            title: playlist.title,
          });

      await shareUrl(playlist.title, text, link);

      gaEventPlaylistShare({
        playlist,
        listName: analyticsListName,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  const copyLink = async (): Promise<void> => {
    try {
      if (playlist === null) throw new Error("Invalid playlist.");

      if (!onboardingGuard(profileGuardMessage)) return;
      // The link fills the clipboard once the share is recorded.
      await copyToClipboard(
        apiClient
          .patch("/webapp/public/playlists/share/:playlistId", {
            param: { playlistId: playlist.id },
            json: roomId === null ? {} : { roomId },
          })
          .then(() => shareLink(playlist))
      );
      // The share is counted: the owner's delete warning reads that count.
      await revalidate("/webapp/public/playlists/:playlistId", {
        param: { playlistId: playlist.id },
      });
      displayToast(
        t("Link copied. Anyone on Juisci with the link can open it."),
        "success"
      );

      gaEventPlaylistShare({
        playlist,
        listName: analyticsListName,
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  return { triggerShare, copyLink };
}
